// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes, useTheme } from "@mui/material/styles";
import { HeartbeatIcon } from "src/assets/icons";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import {
  Bullets,
  Callout,
  Chapter,
  GUIDE_NAMESPACE,
  Paragraph,
  Rich,
  WhoRow,
} from "../GuideText/GuideText";
import { FindWalkthrough } from "../Walkthroughs/SetupWalkthroughs";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

const flowDash = keyframes`to { stroke-dashoffset: -24; }`;

const SOURCES = [
  { title: "Machines", subtitle: "OCT, fundus, visual field", y: 20 },
  { title: "Worklist", subtitle: "today's booked patients", y: 104 },
  { title: "PAC server / folder", subtitle: "your clinic's archive", y: 188 },
];

const IN_PATHS = [
  "M200 49 C250 49 260 112 298 116",
  "M200 133 L298 133",
  "M200 210 C250 210 260 154 298 150",
  "M460 122 L568 122",
];

const DataFlowDiagram = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { surface, onSurface, mutedText, pageWash, dividerBorder } =
    getSurfaceTokens(theme);
  const line = theme.palette.mode === "dark" ? "#5B6D7A" : "rgba(0, 0, 0, 0.2)";

  return (
    <Box
      role="img"
      aria-label={t(
        "Exams travel from the clinic's machines, schedule and archive to the hub PC, then to RetinaLogik. Finished reports travel back to the archive or a folder."
      )}
      sx={{
        backgroundColor: surface,
        border: dividerBorder,
        borderRadius: "12px",
        padding: "12px",
        "& .flow-moving": {
          strokeDasharray: "6 6",
          animation: `${flowDash} 1s linear infinite`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .flow-moving": { animation: "none" },
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 760 270"
        sx={{ width: "100%", height: "auto", display: "block" }}
      >
        <defs>
          <marker
            id="guide-flow-in"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto"
          >
            <path d="M0 0L10 5L0 10z" fill={ACCENT_TEAL} />
          </marker>
          <marker
            id="guide-flow-out"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto"
          >
            <path d="M0 0L10 5L0 10z" fill="#E2772E" />
          </marker>
        </defs>
        <g fontSize="13" fontFamily="Verdana, sans-serif" fill={onSurface}>
          {SOURCES.map((source) => (
            <g key={source.title}>
              <rect
                x="10"
                y={source.y}
                width="190"
                height="58"
                rx="8"
                fill={pageWash}
                stroke={line}
              />
              <text x="58" y={source.y + 25} fontWeight="700">
                {t(source.title)}
              </text>
              <text x="58" y={source.y + 43} fontSize="11" fill={mutedText}>
                {t(source.subtitle)}
              </text>
              <circle
                cx="35"
                cy={source.y + 29}
                r="9"
                fill="none"
                stroke={ACCENT_TEAL}
                strokeWidth="2"
              />
            </g>
          ))}
          <rect x="300" y="90" width="160" height="86" rx="10" fill="#202338" />
          <text
            x="380"
            y="126"
            textAnchor="middle"
            fill="#FFF"
            fontWeight="700"
          >
            {t("Hub PC")}
          </text>
          <text
            x="380"
            y="146"
            textAnchor="middle"
            fill="#C9CDDA"
            fontSize="11"
          >
            {t("at your clinic, always on")}
          </text>
          <rect
            x="570"
            y="90"
            width="180"
            height="86"
            rx="10"
            fill={surface}
            stroke="#3179FE"
            strokeWidth="2"
          />
          <text x="660" y="126" textAnchor="middle" fontWeight="700">
            RetinaLogik
          </text>
          <text
            x="660"
            y="146"
            textAnchor="middle"
            fill={mutedText}
            fontSize="11"
          >
            {t("Clinic Portal, reports")}
          </text>
          {IN_PATHS.map((path) => (
            <path
              key={path}
              className="flow-moving"
              d={path}
              stroke={ACCENT_TEAL}
              strokeWidth="2.5"
              fill="none"
              markerEnd="url(#guide-flow-in)"
            />
          ))}
          <path
            className="flow-moving"
            d="M660 178 C660 250 420 262 206 232"
            stroke="#E2772E"
            strokeWidth="2.5"
            fill="none"
            markerEnd="url(#guide-flow-out)"
          />
          <text
            x="514"
            y="112"
            textAnchor="middle"
            fontSize="11"
            fill={mutedText}
          >
            {t("exams")}
          </text>
          <text
            x="520"
            y="226"
            textAnchor="middle"
            fontSize="11"
            fill={mutedText}
          >
            {t("finished reports come back")}
          </text>
        </g>
      </Box>
      <Box
        display="flex"
        gap="18px"
        flexWrap="wrap"
        fontSize="13px"
        color={mutedText}
        padding="6px 6px 2px"
      >
        {[
          { color: ACCENT_TEAL, label: "Into RetinaLogik" },
          { color: "#E2772E", label: "Back to your clinic" },
        ].map((item) => (
          <Box key={item.label} display="flex" alignItems="center" gap="6px">
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: item.color,
              }}
            />
            {t(item.label)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const WhatChapter = () => (
  <Chapter
    id="what"
    eyebrow="Start here"
    icon={<HeartbeatIcon width="16px" height="16px" />}
    title="What the Data Hub does"
  >
    <Paragraph
      lead
      text="The Data Hub is a small program that runs quietly on one Windows computer at your clinic. We call that computer the <b>hub PC</b>. It collects exams from your machines and sends them to RetinaLogik. When RetinaLogik has finished a report, the hub can put it back where your clinic keeps its records."
    />
    <Paragraph text="You set everything up from the <b>Data Hub</b> page of the Clinic Portal. The hub PC itself has no screens to learn: once it is installed, it just runs." />
    <DataFlowDiagram />
    <Paragraph text="In short, the hub does four jobs:" />
    <Bullets
      items={[
        "It picks up exams from your machines, shared folders and archive.",
        "It reads your daily schedule, so each exam lands on the right patient.",
        "It sends all of that to RetinaLogik safely.",
        "It sends finished RetinaLogik reports back to your archive or to a folder.",
      ]}
    />
    <Callout
      tone="tip"
      text="If the internet drops, nothing is lost. The hub keeps exams in a waiting line and sends them when the connection is back."
    />
  </Chapter>
);

const CHECKLIST = [
  "<b>A Windows PC that stays on.</b> It should be on the same network as your machines. A front-desk or server PC is good. A laptop that goes home at night is not.",
  "<b>Internet on that PC.</b> The hub talks to RetinaLogik over the internet.",
  '<b>Permission to install programs.</b> Windows will ask "Do you want to allow this app to make changes?". Someone who can say Yes must be there.',
  "<b>A fixed address for the hub PC.</b> Ask your IT person to give the hub PC a <fixedip>fixed IP address</fixedip>. Machines that send exams to the hub need to find it at the same address every day.",
  "<b>Details from IT or your archive vendor</b>, only if you connect a <pacserver>PAC server</pacserver> or a machine over <dicom>DICOM</dicom>: its address, its port and its AE title. The setup screens tell you exactly which ones.",
];

const Checklist = () => {
  const theme = useTheme();
  const { surface, dividerBorder } = getSurfaceTokens(theme);

  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {CHECKLIST.map((item) => (
        <Box
          component="li"
          key={item}
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            backgroundColor: surface,
            border: dividerBorder,
            borderRadius: "8px",
            padding: "10px 12px",
          }}
        >
          <Box component="span" color={ACCENT_TEAL} fontWeight={700} mt="1px">
            ✓
          </Box>
          <Typography fontSize="14px" lineHeight={1.6}>
            <Rich text={item} />
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const BeforeChapter = () => (
  <Chapter id="before" eyebrow="Start here" title="Before you start">
    <Paragraph text="Check these few things first. It saves time later." />
    <Checklist />
    <Paragraph text="Throughout this manual, each task is marked to show who usually does it:" />
    <WhoRow
      items={[
        { kind: "you", text: "You can do this" },
        { kind: "it", text: "IT person or technician" },
      ]}
    />
  </Chapter>
);

export const FindChapter = () => (
  <Chapter id="find" eyebrow="Start here" title="Find the Data Hub">
    <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
    <Paragraph text="Sign in to the Clinic Portal. The <b>Data Hub</b> tab is in the dark bar at the top, next to Dashboard and Patients." />
    <FindWalkthrough />
    <Callout
      tone="warning"
      text="No <b>Data Hub</b> tab? Your clinic's plan does not include it yet. Contact RetinaLogik to turn it on."
    />
  </Chapter>
);
